import { format } from 'date-fns';
import { XMarkIcon } from '@heroicons/react/24/outline';

import RevertIcon from '~/shared/assets/icons/revert.svg';
import CheckIcon from '~/shared/assets/icons/check.svg';
import Button from '~/shared/components/ButtonsV2';
import Typography from '~/shared/components/Typography';

interface ConfigurationFooterProps {
  lastUpdated: string;
  hasChanges: boolean;
  hasOverrides: boolean;
  isFormValid: boolean;
  onCancel: () => void;
  onSave: () => void;
  onRevertAll: () => void;
  isSaving?: boolean;
  isRevertingAll?: boolean;
}

export default function ConfigurationFooter({
  lastUpdated,
  hasChanges,
  hasOverrides,
  isFormValid,
  onCancel,
  onSave,
  onRevertAll,
  isSaving = false,
  isRevertingAll = false,
}: ConfigurationFooterProps) {
  const formattedDate = lastUpdated ? format(new Date(lastUpdated), 'dd/MM/yyyy, hh:mm a') : '';

  const saveButtonDisabled = !hasChanges || isSaving || !isFormValid;
  const revertButtonDisabled = !hasOverrides || isRevertingAll;

  const handleSaveClick = (_e: React.MouseEvent<HTMLButtonElement>) => {
    if (saveButtonDisabled) {
      return;
    }

    if (typeof onSave === 'function') {
      onSave();
    }
  };

  const handleRevertClick = (_e: React.MouseEvent<HTMLButtonElement>) => {
    if (revertButtonDisabled) {
      return;
    }

    if (typeof onRevertAll === 'function') {
      onRevertAll();
    }
  };

  return (
    <div className="flex items-center justify-between px-6 py-4 border-t border-primary-70 bg-white">
      <Button
        variant="third"
        onClick={onCancel}
        leftIcon={<XMarkIcon className="w-4 h-4" />}
        className="px-5"
        disabled={!hasChanges}
      >
        Cancel
      </Button>
      <div className="flex items-center gap-4">
        {lastUpdated && (
          <Typography variant="caption" color="text-gray-700">
            Last Update: {formattedDate}
          </Typography>
        )}
        <Button
          variant="third"
          onClick={handleRevertClick}
          disabled={revertButtonDisabled}
          leftIcon={
            isRevertingAll ? (
              <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            ) : (
              <RevertIcon className="w-4 h-4 " />
            )
          }
          className="group"
        >
          Revert all to default
        </Button>
        <Button
          variant="primary"
          onClick={handleSaveClick}
          disabled={saveButtonDisabled}
          className="px-7"
          leftIcon={
            isSaving ? (
              <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            ) : (
              <CheckIcon className="w-4 h-4 " />
            )
          }
        >
          Save
        </Button>
      </div>
    </div>
  );
}
